import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  ArrowDownTrayIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  ChartBarIcon,
  CubeIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { AnalyticsService, TrendBucket } from '../../services/analytics.service';
import {
  badgeClass,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';

type RangeKey = '7d' | '30d' | '90d' | '12m' | 'all' | 'custom';
type TabKey = 'sales' | 'inventory' | 'finance';

const RANGES: { key: RangeKey; label: string; days: number | null }[] = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
  { key: '12m', label: '12 months', days: 365 },
  { key: 'all', label: 'All time', days: null },
  { key: 'custom', label: 'Custom', days: null },
];

const TABS: { key: TabKey; label: string; hint: string }[] = [
  { key: 'sales', label: 'Sales', hint: 'Trend, products, customers' },
  { key: 'inventory', label: 'Inventory', hint: 'Stock value and movement' },
  { key: 'finance', label: 'Finance', hint: 'Sales against purchases, GST' },
];

// Chart colours, the same two the home dashboard uses
const SALES_COLOR = '#4f46e5';
const PURCHASE_COLOR = '#0d9488';

const STATUS_META: Record<string, { label: string; color: string }> = {
  quote: { label: 'Quote', color: 'text-gray-700 bg-gray-50 ring-gray-500/20' },
  order: { label: 'Order', color: 'text-blue-700 bg-blue-50 ring-blue-600/20' },
  confirmed: { label: 'Confirmed', color: 'text-indigo-700 bg-indigo-50 ring-indigo-600/20' },
  shipped: { label: 'Shipped', color: 'text-purple-700 bg-purple-50 ring-purple-600/20' },
  delivered: { label: 'Delivered', color: 'text-teal-700 bg-teal-50 ring-teal-600/20' },
  completed: { label: 'Completed', color: 'text-green-700 bg-green-50 ring-green-600/20' },
  cancelled: { label: 'Cancelled', color: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20' },
};
// Statuses that are not counted as sales
const NOT_SALES = ['quote', 'cancelled'];

const toDateInput = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const parseDay = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const formatDay = (value: string) =>
  parseDay(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const formatMonth = (value: string) =>
  parseDay(`${value.slice(0, 7)}-01`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

// The label of one bar of the sales trend
const formatBucket = (value: string, bucket: TrendBucket) => {
  if (bucket === 'month') return formatMonth(value);
  const label = parseDay(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  return bucket === 'week' ? `Wk of ${label}` : label;
};

const formatCompactMoney = (value: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', notation: 'compact', maximumFractionDigits: 1 }).format(
    Number(value || 0),
  );

const BUCKET_TITLES: Record<TrendBucket, string> = { day: 'Sales by day', week: 'Sales by week', month: 'Sales by month' };

const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const tdClass = 'whitespace-nowrap px-4 py-3 text-sm text-gray-700';
const numClass = `${tdClass} text-right tabular-nums`;
const axisTick = { fontSize: 12, fill: '#6b7280' };

// A titled card around one chart or table
const Panel: React.FC<{ title: string; description?: string; className?: string; children: React.ReactNode }> = ({
  title,
  description,
  className,
  children,
}) => (
  <section className={cn('overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200', className)}>
    <div className="border-b border-gray-200 px-4 py-3">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
    </div>
    {children}
  </section>
);

const EmptyState: React.FC<{ icon: React.ElementType; title: string; text: string }> = ({ icon: Icon, title, text }) => (
  <div className="flex h-64 flex-col items-center justify-center px-4 text-center">
    <Icon className="h-10 w-10 text-gray-300" />
    <p className="mt-2 text-sm font-medium text-gray-900">{title}</p>
    <p className="mt-1 text-sm text-gray-500">{text}</p>
  </div>
);

const EmptyRow: React.FC<{ colSpan: number; text: string }> = ({ colSpan, text }) => (
  <tr>
    <td className="px-4 py-8 text-center text-sm text-gray-500" colSpan={colSpan}>
      {text}
    </td>
  </tr>
);

// A value with a thin bar showing its share of the largest value in the list
const ShareBar: React.FC<{ value: number; max: number; color?: string }> = ({ value, max, color = 'bg-indigo-500' }) => (
  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
    <div className={`h-full rounded-full ${color}`} style={{ width: `${max > 0 ? Math.max(2, (value / max) * 100) : 0}%` }} />
  </div>
);

const MoneyTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md bg-white px-3 py-2 text-sm shadow-lg ring-1 ring-gray-200">
      <p className="font-medium text-gray-900">{label}</p>
      {payload.map((entry: any) => (
        <p key={entry.dataKey} className="mt-1 flex items-center gap-2 text-gray-700">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: entry.color }} />
          {entry.name}
          <span className="ml-auto pl-4 font-medium tabular-nums text-gray-900">
            {entry.payload.isUnits ? formatNumber(entry.value) : formatMoney(entry.value)}
          </span>
        </p>
      ))}
      {typeof payload[0].payload.orders === 'number' && (
        <p className="mt-1 text-xs text-gray-500">
          {formatNumber(payload[0].payload.orders)} {payload[0].payload.orders === 1 ? 'order' : 'orders'}
        </p>
      )}
    </div>
  );
};

const AnalyticsDashboard: React.FC = () => {
  const [range, setRange] = useState<RangeKey>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [tab, setTab] = useState<TabKey>('sales');
  const [isExporting, setIsExporting] = useState(false);

  // The first and last day of the period, both included
  const today = new Date();
  const preset = RANGES.find((entry) => entry.key === range);
  const fromDate =
    range === 'custom'
      ? customFrom || undefined
      : preset?.days
        ? toDateInput(new Date(today.getFullYear(), today.getMonth(), today.getDate() - (preset.days - 1)))
        : undefined;
  const toDate = range === 'custom' ? customTo || undefined : range === 'all' ? undefined : toDateInput(today);
  const dateError = fromDate && toDate && toDate < fromDate ? 'The "to" date cannot be before the "from" date' : '';
  const enabled = !dateError;

  const metricsQuery = useQuery({
    queryKey: ['analytics', 'dashboard', fromDate, toDate],
    queryFn: () => AnalyticsService.getDashboardMetrics(fromDate, toDate),
    enabled,
    placeholderData: keepPreviousData,
  });
  const salesQuery = useQuery({
    queryKey: ['analytics', 'sales', fromDate, toDate],
    queryFn: () => AnalyticsService.getSalesAnalytics(fromDate, toDate),
    enabled: enabled && tab === 'sales',
    placeholderData: keepPreviousData,
  });
  const inventoryQuery = useQuery({
    queryKey: ['analytics', 'inventory', fromDate, toDate],
    queryFn: () => AnalyticsService.getInventoryAnalytics(fromDate, toDate),
    enabled: enabled && tab === 'inventory',
    placeholderData: keepPreviousData,
  });
  const financeQuery = useQuery({
    queryKey: ['analytics', 'financial', fromDate, toDate],
    queryFn: () => AnalyticsService.getFinancialAnalytics(fromDate, toDate),
    enabled: enabled && tab === 'finance',
    placeholderData: keepPreviousData,
  });

  const metrics = metricsQuery.data;
  const activeQuery = tab === 'sales' ? salesQuery : tab === 'inventory' ? inventoryQuery : financeQuery;

  const periodText =
    !fromDate && !toDate
      ? 'all time'
      : `${fromDate ? formatDay(fromDate) : 'the start'} to ${toDate ? formatDay(toDate) : 'today'}`;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob =
        tab === 'sales'
          ? await AnalyticsService.exportSalesReport(fromDate, toDate)
          : tab === 'inventory'
            ? await AnalyticsService.exportInventoryReport()
            : await AnalyticsService.exportFinancialReport(fromDate, toDate);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${tab}-report-${fromDate || 'start'}-to-${toDate || toDateInput(today)}.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('Report exported');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not export the report'));
    } finally {
      setIsExporting(false);
    }
  };

  const renderGrowth = (value: number | null | undefined, goodWhenUp: boolean) => {
    if (value === null || value === undefined) {
      return <span className="text-xs text-gray-500">No earlier period to compare</span>;
    }
    const up = value >= 0;
    const good = up === goodWhenUp;
    const Icon = up ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
    return (
      <span className={`inline-flex items-center text-xs font-medium ${value === 0 ? 'text-gray-500' : good ? 'text-green-700' : 'text-red-700'}`}>
        <Icon className="mr-1 h-3.5 w-3.5" />
        {formatNumber(Math.abs(value), 1)}% {up ? 'up' : 'down'} on the period before
      </span>
    );
  };

  const kpis = [
    {
      label: 'Sales',
      value: formatMoney(metrics?.totalSales ?? 0),
      detail: `${formatNumber(metrics?.salesOrders ?? 0)} ${metrics?.salesOrders === 1 ? 'order' : 'orders'} · average ${formatMoney(metrics?.averageOrderValue ?? 0)}`,
      footer: renderGrowth(metrics?.salesGrowth, true),
      icon: BanknotesIcon,
      tone: 'text-indigo-600 bg-indigo-50',
    },
    {
      label: 'Purchases',
      value: formatMoney(metrics?.totalPurchases ?? 0),
      detail: `${formatNumber(metrics?.purchaseOrders ?? 0)} approved or received ${metrics?.purchaseOrders === 1 ? 'order' : 'orders'}`,
      footer: renderGrowth(metrics?.purchaseGrowth, false),
      icon: ShoppingCartIcon,
      tone: 'text-teal-600 bg-teal-50',
    },
    {
      label: 'Sales minus Purchases',
      value: formatMoney(metrics?.netPosition ?? 0),
      detail: metrics && metrics.totalSales > 0 ? `${formatNumber(metrics.profitMargin, 1)}% of sales` : 'No sales in this period',
      footer: <span className="text-xs text-gray-500">Orders placed, not money received</span>,
      icon: ArrowsRightLeftIcon,
      tone: (metrics?.netPosition ?? 0) < 0 ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50',
    },
    {
      label: 'Invoiced',
      value: formatMoney(metrics?.invoicedValue ?? 0),
      detail: `${formatNumber(metrics?.totalInvoices ?? 0)} issued ${metrics?.totalInvoices === 1 ? 'invoice' : 'invoices'}`,
      footer: (
        <span className="text-xs text-gray-500">
          {metrics?.openQuotes
            ? `${formatNumber(metrics.openQuotes)} open ${metrics.openQuotes === 1 ? 'quote' : 'quotes'} worth ${formatMoney(metrics.openQuoteValue)}`
            : 'No open quotes from this period'}
        </span>
      ),
      icon: DocumentTextIcon,
      tone: 'text-blue-600 bg-blue-50',
    },
  ];

  const renderSales = () => {
    const sales = salesQuery.data;
    if (!sales) return null;
    const trend = sales.trend.map((row) => ({ ...row, label: formatBucket(row.period, sales.bucket) }));
    const maxProduct = Math.max(0, ...sales.topProducts.map((row) => row.revenue));
    const maxCustomer = Math.max(0, ...sales.customerAnalytics.map((row) => row.totalValue));

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <Panel
            title={BUCKET_TITLES[sales.bucket]}
            description="Value of orders placed, without quotes and cancelled orders."
            className="xl:col-span-2"
          >
            {trend.length === 0 ? (
              <EmptyState icon={ChartBarIcon} title="No sales in this period" text="Try a longer period, or All time." />
            ) : (
              <div className="h-72 p-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trend} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={formatCompactMoney} width={64} />
                    <Tooltip content={<MoneyTooltip />} cursor={{ fill: '#f9fafb' }} />
                    <Bar dataKey="amount" name="Sales" fill={SALES_COLOR} radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="Orders by status" description="Everything raised in the period, quotes included.">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Status</th>
                    <th className={`${thClass} text-right`}>Orders</th>
                    <th className={`${thClass} text-right`}>Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {sales.salesByStatus.length === 0 ? (
                    <EmptyRow colSpan={3} text="Nothing was raised in this period" />
                  ) : (
                    sales.salesByStatus.map((row) => {
                      const meta = STATUS_META[row.status] || { label: row.status, color: STATUS_META.quote.color };
                      return (
                        <tr key={row.status}>
                          <td className={tdClass}>
                            <span className={`${badgeClass} ${meta.color}`}>{meta.label}</span>
                            {NOT_SALES.includes(row.status) && <span className="ml-2 text-xs text-gray-500">not counted as sales</span>}
                          </td>
                          <td className={numClass}>{formatNumber(row.count)}</td>
                          <td className={`${numClass} font-medium text-gray-900`}>{formatMoney(row.value)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Panel title="Top products" description="The ten products that brought in the most.">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Product</th>
                    <th className={`${thClass} text-right`}>Quantity</th>
                    <th className={`${thClass} w-2/5 text-right`}>Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {sales.topProducts.length === 0 ? (
                    <EmptyRow colSpan={3} text="No products were sold in this period" />
                  ) : (
                    sales.topProducts.map((row) => (
                      <tr key={row.productId}>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{row.productName}</div>
                          {row.sku && <div className="font-mono text-xs text-gray-500">{row.sku}</div>}
                        </td>
                        <td className={numClass}>{formatNumber(row.quantity)}</td>
                        <td className="px-4 py-3 text-right text-sm">
                          <span className="font-medium tabular-nums text-gray-900">{formatMoney(row.revenue)}</span>
                          <ShareBar value={row.revenue} max={maxProduct} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="Top customers" description="The ten customers who ordered the most.">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Customer</th>
                    <th className={`${thClass} text-right`}>Orders</th>
                    <th className={`${thClass} w-2/5 text-right`}>Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {sales.customerAnalytics.length === 0 ? (
                    <EmptyRow colSpan={3} text="No customer ordered in this period" />
                  ) : (
                    sales.customerAnalytics.map((row) => (
                      <tr key={row.customerId}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.customerName}</td>
                        <td className={numClass}>{formatNumber(row.totalOrders)}</td>
                        <td className="px-4 py-3 text-right text-sm">
                          <span className="font-medium tabular-nums text-gray-900">{formatMoney(row.totalValue)}</span>
                          <ShareBar value={row.totalValue} max={maxCustomer} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      </div>
    );
  };

  const renderInventory = () => {
    const inventory = inventoryQuery.data;
    if (!inventory) return null;
    const maxCategory = Math.max(0, ...inventory.inventoryValue.map((row) => row.value));
    const movement = inventory.stockMovement.map((row) => ({ ...row, label: formatMonth(row.month), isUnits: true }));
    const hasMovement = movement.some((row) => row.stockIn > 0 || row.stockOut > 0);

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          {[
            { label: 'Stock value at cost', value: formatMoney(inventory.totals.stockValue), tone: 'bg-indigo-50 text-indigo-900' },
            { label: 'Active products', value: formatNumber(inventory.totals.totalProducts), tone: 'bg-gray-50 text-gray-900' },
            {
              label: 'At or below minimum',
              value: formatNumber(inventory.totals.lowStock),
              tone: inventory.totals.lowStock > 0 ? 'bg-yellow-50 text-yellow-900' : 'bg-gray-50 text-gray-900',
            },
            {
              label: 'Out of stock',
              value: formatNumber(inventory.totals.outOfStock),
              tone: inventory.totals.outOfStock > 0 ? 'bg-red-50 text-red-900' : 'bg-gray-50 text-gray-900',
            },
          ].map((item) => (
            <div key={item.label} className={`rounded-lg px-4 py-3 ring-1 ring-gray-200 ${item.tone}`}>
              <p className="text-xs font-medium uppercase tracking-wide opacity-70">{item.label}</p>
              <p className="mt-1 text-xl font-semibold tabular-nums">{item.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Panel title="Stock movement" description="Units received and issued in each of the last six months, from the stock ledger.">
            {!hasMovement ? (
              <EmptyState icon={CubeIcon} title="No stock has moved in six months" text="Receipts and issues show here as they are recorded." />
            ) : (
              <div className="h-72 p-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={movement} margin={{ top: 5, right: 8, left: 0, bottom: 0 }} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} width={48} />
                    <Tooltip content={<MoneyTooltip />} cursor={{ fill: '#f9fafb' }} />
                    <Legend iconType="square" formatter={(value) => <span className="text-sm text-gray-700">{value}</span>} />
                    <Bar dataKey="stockIn" name="Units in" fill={SALES_COLOR} radius={[4, 4, 0, 0]} maxBarSize={32} isAnimationActive={false} />
                    <Bar dataKey="stockOut" name="Units out" fill={PURCHASE_COLOR} radius={[4, 4, 0, 0]} maxBarSize={32} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="Stock value by category" description="What the stock on hand cost, as of now.">
            {inventory.inventoryValue.length === 0 ? (
              <EmptyState icon={CubeIcon} title="No products yet" text="Stock value shows here once products are added." />
            ) : (
              <ul className="divide-y divide-gray-100">
                {inventory.inventoryValue.map((row) => (
                  <li key={row.category} className="px-4 py-3 text-sm">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium text-gray-900">{row.category}</span>
                      <span className="font-medium tabular-nums text-gray-900">{formatMoney(row.value)}</span>
                    </div>
                    <ShareBar value={row.value} max={maxCategory} />
                    <p className="mt-1 text-xs text-gray-500">
                      {formatNumber(row.items)} {row.items === 1 ? 'product' : 'products'}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Panel title="Low stock" description="Products at or below their minimum stock, the shortest first.">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Product</th>
                    <th className={`${thClass} text-right`}>In Stock</th>
                    <th className={`${thClass} text-right`}>Minimum</th>
                    <th className={`${thClass} text-right`}>Short By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {inventory.lowStockItems.length === 0 ? (
                    <EmptyRow colSpan={4} text="Every product is above its minimum stock" />
                  ) : (
                    inventory.lowStockItems.map((row) => (
                      <tr key={row.productId}>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{row.productName}</div>
                          <div className="font-mono text-xs text-gray-500">{row.sku}</div>
                        </td>
                        <td className={`${numClass} ${row.currentStock <= 0 ? 'font-medium text-red-700' : ''}`}>
                          {formatNumber(row.currentStock)} {row.unit}
                        </td>
                        <td className={numClass}>{formatNumber(row.minimumStock)}</td>
                        <td className={`${numClass} font-medium text-yellow-800`}>
                          {formatNumber(Math.max(0, row.minimumStock - row.currentStock))}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="Fastest moving products" description={`Units sold, ${periodText}.`}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Product</th>
                    <th className={`${thClass} text-right`}>Units Sold</th>
                    <th className={`${thClass} text-right`}>Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {inventory.fastMovingProducts.length === 0 ? (
                    <EmptyRow colSpan={3} text="No products were sold in this period" />
                  ) : (
                    inventory.fastMovingProducts.map((row) => (
                      <tr key={row.productId}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.productName}</td>
                        <td className={numClass}>{formatNumber(row.totalSold)}</td>
                        <td className={`${numClass} font-medium text-gray-900`}>{formatMoney(row.revenue)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      </div>
    );
  };

  const renderFinance = () => {
    const finance = financeQuery.data;
    if (!finance) return null;
    const monthly = finance.monthlyRevenue.map((row) => ({ ...row, label: formatMonth(row.month) }));
    const totals = Object.fromEntries(finance.profitLoss.map((row) => [row.category, row]));
    const totalTax = finance.taxSummary.reduce((sum, row) => sum + row.amount, 0);

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <Panel
            title="Sales and purchases by month"
            description="Orders placed with customers against orders approved with vendors."
            className="xl:col-span-2"
          >
            {monthly.length === 0 ? (
              <EmptyState icon={ChartBarIcon} title="No orders in this period" text="Try a longer period, or All time." />
            ) : (
              <div className="h-72 p-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthly} margin={{ top: 5, right: 8, left: 0, bottom: 0 }} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={formatCompactMoney} width={64} />
                    <Tooltip content={<MoneyTooltip />} cursor={{ fill: '#f9fafb' }} />
                    <Legend iconType="square" formatter={(value) => <span className="text-sm text-gray-700">{value}</span>} />
                    <Bar dataKey="revenue" name="Sales" fill={SALES_COLOR} radius={[4, 4, 0, 0]} maxBarSize={40} isAnimationActive={false} />
                    <Bar dataKey="expenses" name="Purchases" fill={PURCHASE_COLOR} radius={[4, 4, 0, 0]} maxBarSize={40} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="Totals for the period">
            <dl className="space-y-3 p-4 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="flex items-center gap-2 text-gray-600">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: SALES_COLOR }} />
                  Sales
                </dt>
                <dd className="font-medium tabular-nums text-gray-900">{formatMoney(totals.Revenue?.amount ?? 0)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="flex items-center gap-2 text-gray-600">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: PURCHASE_COLOR }} />
                  Purchases
                </dt>
                <dd className="font-medium tabular-nums text-gray-900">{formatMoney(totals.Expenses?.amount ?? 0)}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3 border-t border-gray-200 pt-3">
                <dt className="font-semibold text-gray-900">Sales minus purchases</dt>
                <dd className={`text-lg font-semibold tabular-nums ${(totals.Profit?.amount ?? 0) < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                  {formatMoney(totals.Profit?.amount ?? 0)}
                </dd>
              </div>
              <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
                These are order values, tax included. Payments are not recorded yet, so this is not cash received or
                profit after costs.
              </p>
            </dl>
          </Panel>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Panel title="Invoices issued" description="Invoices dated in the period that were issued, paid ones included.">
            <dl className="grid grid-cols-2 gap-4 p-4">
              {[
                { label: 'Invoices', value: formatNumber(finance.invoices.issued) },
                { label: 'Invoiced value', value: formatMoney(finance.invoices.issuedValue) },
                { label: 'Paid', value: formatMoney(finance.invoices.paidValue) },
                { label: 'Awaiting payment', value: formatMoney(finance.invoices.outstandingValue) },
              ].map((item) => (
                <div key={item.label} className="rounded-lg bg-gray-50 px-4 py-3">
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{item.label}</dt>
                  <dd className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{item.value}</dd>
                </div>
              ))}
            </dl>
          </Panel>

          <Panel title="GST on issued invoices" description="Tax charged to customers in the period.">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Tax</th>
                    <th className={`${thClass} text-right`}>Share</th>
                    <th className={`${thClass} text-right`}>Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {finance.taxSummary.length === 0 ? (
                    <EmptyRow colSpan={3} text="No GST was charged on invoices in this period" />
                  ) : (
                    <>
                      {finance.taxSummary.map((row) => (
                        <tr key={row.taxType}>
                          <td className={`${tdClass} font-medium text-gray-900`}>{row.taxType}</td>
                          <td className={numClass}>{formatNumber(row.percentage, 1)}%</td>
                          <td className={`${numClass} font-medium text-gray-900`}>{formatMoney(row.amount)}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50">
                        <td className={`${tdClass} font-semibold text-gray-900`} colSpan={2}>
                          Total GST
                        </td>
                        <td className={`${numClass} font-semibold text-gray-900`}>{formatMoney(totalTax)}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Analytics</h1>
          <p className="mt-1 text-sm text-gray-600">Sales, stock and finance figures for {periodText}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg bg-gray-100 p-1" role="group" aria-label="Period">
            {RANGES.map((entry) => (
              <button
                key={entry.key}
                type="button"
                aria-pressed={range === entry.key}
                onClick={() => setRange(entry.key)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium',
                  range === entry.key ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                )}
              >
                {entry.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={handleExport} disabled={isExporting || !enabled} className={secondaryButtonClass}>
            <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {range === 'custom' && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-white p-4 text-sm text-gray-600 shadow-sm ring-1 ring-gray-200">
          <label className="flex items-center gap-2">
            From
            <input
              type="date"
              aria-label="Period from"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <label className="flex items-center gap-2">
            to
            <input
              type="date"
              aria-label="Period to"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => setCustomTo(e.target.value)}
              className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          {dateError ? (
            <span className="font-medium text-red-600">{dateError}</span>
          ) : (
            <span className="text-gray-500">Leave a date empty to leave that side open.</span>
          )}
        </div>
      )}

      {/* Headline figures */}
      {metricsQuery.error ? (
        <div className="rounded-lg bg-white px-4 py-8 text-center shadow-sm ring-1 ring-gray-200">
          <p className="text-sm text-red-600">{getApiErrorMessage(metricsQuery.error, 'Could not load the figures')}</p>
        </div>
      ) : (
        <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4', metricsQuery.isFetching && !metricsQuery.isLoading && 'opacity-60')}>
          {kpis.map((kpi) => (
            <div key={kpi.label} className="flex items-start gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${kpi.tone}`}>
                <kpi.icon className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-gray-500">{kpi.label}</p>
                <p className="truncate text-xl font-semibold text-gray-900 tabular-nums">{metricsQuery.isLoading ? '—' : kpi.value}</p>
                <p className="truncate text-xs text-gray-500">{metricsQuery.isLoading ? ' ' : kpi.detail}</p>
                <div className="mt-1">{metricsQuery.isLoading ? null : kpi.footer}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sections */}
      <div className="inline-flex rounded-lg bg-gray-100 p-1" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            onClick={() => setTab(entry.key)}
            className={cn(
              'rounded-md px-4 py-2 text-sm font-semibold',
              tab === entry.key ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900',
            )}
          >
            {entry.label}
            <span className="ml-2 hidden text-xs font-normal text-gray-500 lg:inline">{entry.hint}</span>
          </button>
        ))}
      </div>

      {dateError ? (
        <div className="rounded-lg bg-white px-4 py-12 text-center shadow-sm ring-1 ring-gray-200">
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-2 text-sm font-medium text-gray-900">Check the dates</p>
          <p className="mt-1 text-sm text-gray-500">{dateError}.</p>
        </div>
      ) : activeQuery.isLoading ? (
        <div className="rounded-lg bg-white px-4 py-12 text-center text-sm text-gray-500 shadow-sm ring-1 ring-gray-200">
          Loading figures...
        </div>
      ) : activeQuery.error ? (
        <div className="rounded-lg bg-white px-4 py-12 text-center shadow-sm ring-1 ring-gray-200">
          <p className="text-sm text-red-600">{getApiErrorMessage(activeQuery.error, 'Could not load the figures')}</p>
        </div>
      ) : (
        <div className={activeQuery.isFetching ? 'opacity-60' : ''}>
          {tab === 'sales' ? renderSales() : tab === 'inventory' ? renderInventory() : renderFinance()}
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
